import React, { useState, useEffect, useReducer, useRef } from "react";
import { Box, CircularProgress, Alert } from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import { API_BASE_URL } from "../../../../config";
import Generale from "../../visuredetails/Generale/index.js";
import {
  generateInsuranceAnalysis,
  isGeminiConfigured,
} from "../../../../services/geminiService";
import { setAiAnalysis } from "../../../../redux/slices/visureAiAnalysis";

// Reducer per gestire lo stato della visura (stesso reducer di visuredetails)
function reducer(state, action) {
  switch (action.type) {
    case "header": {
      return {
        data: {
          companyId: action.value.companyId,
          correlationId: action.value.correlationId,
          dataOfOrder: action.value.dateOfOrder,
          language: action.value.language,
          report: action.value.report,
          userId: action.value.userId,
        },
        analitics: action.value.analitics || {},
      };
    }
    default: {
      throw Error("Unknown action: " + action.type);
    }
  }
}

const initialState = {
  data: {
    companyId: "",
    correlationId: "",
    dataOfOrder: "",
    language: "",
    report: {
      alternateSummary: {},
      companySummary: {},
      companySummaryExtra: {},
      creditScore: {
        currentCreditRating: {
          commonDescription: "",
          commonValue: "",
          creditLimit: {
            currency: "",
            value: "",
          },
          providerDescription: "",
          providerValue: {
            maxValue: "",
            minValue: "",
            value: "",
          },
        },
        currentContractLimit: {
          currency: "",
          value: "",
        },
        latestRatingChangeDate: "",
        previousCreditRating: {
          commonDescription: "",
          commonValue: "",
          creditLimit: {
            currency: "",
            value: "",
          },
          providerDescription: "",
          providerValue: {
            maxValue: "",
            minValue: "",
            value: "",
          },
        },
      },
      financialStatements: [],
      language: "",
      localFinancialStatements: [],
      localIndicators: [],
      otherInformation: {
        employeesInformation: [],
      },
    },
    userId: "",
  },
  analitics: {},
};

const RiskRadar = ({ dataset, fullData }) => {
  const [state, dispatch] = useReducer(reducer, initialState);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const dataJSON = useRef([]);
  const dispatchRedux = useDispatch();

  // TODO: Rimuovere hardcode quando il backend sarà pronto
  // HARDCODED per il momento - sostituire con il valore reale quando disponibile
  const idUidAzienda = "HARDCODED_ID_UID"; // Sostituire con: dataset?.idUID || dataset?.iduid || ...
  
  // Recupera l'analisi dal Redux store
  const aiAnalysis = useSelector(
    (state) => state.visureAiAnalysis.analyses[idUidAzienda],
  );

  async function visureGetDetails() {
    if (!idUidAzienda) {
      setError("ID azienda non disponibile");
      setIsLoading(false);
      return;
    }

    console.log("RiskRadar - idUidAzienda:", idUidAzienda);
    console.log("RiskRadar - dataset:", dataset);
    console.log("RiskRadar - fullData:", fullData);
    console.log("RiskRadar - fullData.tabs:", fullData?.tabs);

    const token = sessionStorage.getItem("token");

    try {
      const requestOptions = {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          authorization: "Bearer " + token,
        },
        body: JSON.stringify({
          token,
          // idUidAzienda: idUidAzienda,
          idUidAzienda: "66A63CFB-8F28-4A9F-B07A-AC6950C4A258" // VALORE HARDCODATO, MODIFICARE
        }),
      };

      const response = await fetch(
        `${API_BASE_URL}/api/visureGetDetails`,
        requestOptions
      );

      const responseData = await response.json();
      
      if (responseData.status === 0) {
        // Utente non autorizzato ad accedere ai dati della visura
        setError(responseData.message || "Non sei autorizzato ad accedere ai dati di Risk Radar per questa azienda");
        setIsLoading(false);
        return;
      }
      
      if (responseData.status === -1) {
        setError("Errore nel caricamento dei dati della visura");
        setIsLoading(false);
        return;
      }

      dataJSON.current = {
        ...responseData.data.visura,
        analitics: responseData.data.analitics || {},
      };
      
      dispatch({
        type: "header",
        value: dataJSON.current,
      });
      
      // Gestione analitics come in Generale
      const receivedAnalitics = responseData.data.analitics || {};
      const hasValidAnalytics =
        receivedAnalitics &&
        Object.keys(receivedAnalitics).length > 0 &&
        receivedAnalitics.copertureAssicurative &&
        Array.isArray(receivedAnalitics.copertureAssicurative) &&
        receivedAnalitics.copertureAssicurative.length > 0;

      console.log("RiskRadar - Analitics ricevute:", receivedAnalitics);
      console.log("RiskRadar - hasValidAnalytics:", hasValidAnalytics);
      console.log("RiskRadar - aiAnalysis esistente:", aiAnalysis);

      // Se abbiamo analitics validi dall'API, usali e salvali nel Redux
      if (hasValidAnalytics && !aiAnalysis) {
        console.log("RiskRadar - Using analitics from API:", receivedAnalitics);
        dispatchRedux(setAiAnalysis({ idUID: "66A63CFB-8F28-4A9F-B07A-AC6950C4A258", analysis: receivedAnalitics }));
        setIsLoading(false);
        return;
      }

      // Se abbiamo già l'analisi nel Redux, non ricarichiamo
      if (aiAnalysis) {
        console.log("RiskRadar - Using existing analysis from Redux");
        setIsLoading(false);
        return;
      }

      // Se non abbiamo né analitics dall'API né nel Redux, genera con Gemini
      if (!isGeminiConfigured()) {
        setError("Gemini AI non configurato. Impossibile generare l'analisi.");
        setIsLoading(false);
        return;
      }

      try {
        console.log("RiskRadar - Generating AI analysis with Gemini...");
        const analysis = await generateInsuranceAnalysis(responseData.data.visura.report);
        console.log("RiskRadar - AI Analysis generated:", analysis);

        // Salva l'analisi nel database via API
        try {
          await fetch(`${API_BASE_URL}/api/visureAziendeSave`, {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              authorization: "Bearer " + token,
            },
            body: JSON.stringify({
              idUid: "66A63CFB-8F28-4A9F-B07A-AC6950C4A258",
              dataAnalitics: analysis,
            }),
          });
          console.log("RiskRadar - Analysis saved to database");
        } catch (saveError) {
          console.error("RiskRadar - Error saving analysis to database:", saveError);
          // Non blocchiamo il flusso se il salvataggio fallisce
        }

        // Salva l'analisi nel Redux store
        dispatchRedux(setAiAnalysis({ idUID: "66A63CFB-8F28-4A9F-B07A-AC6950C4A258", analysis }));
        
        // Aggiorna lo state locale con l'analisi generata
        dataJSON.current.analitics = analysis;
        dispatch({
          type: "header",
          value: dataJSON.current,
        });

        setIsLoading(false);
      } catch (aiError) {
        console.error("RiskRadar - Error generating AI analysis:", aiError);
        setError(`Errore durante la generazione dell'analisi AI: ${aiError.message}`);
        setIsLoading(false);
      }
      
      setIsLoading(false);
    } catch (error) {
      console.error("Errore nel caricamento della visura:", error);
      setError("Errore nel caricamento dei dati della visura");
      setIsLoading(false);
    }
  }

  useEffect(() => {
    visureGetDetails();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (isLoading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "400px",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  if (!state.data.report || !state.data.report.companySummary) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="warning">
          Dati della visura non disponibili per questa azienda
        </Alert>
      </Box>
    );
  }

  // Usa aiAnalysis dal Redux se disponibile, altrimenti usa state.analitics
  const analyticsToUse = aiAnalysis || state.analitics || {};

  return (
    <Box sx={{ width: "100%" }}>
      <Generale report={state.data.report} analitics={analyticsToUse} />
    </Box>
  );
};

export default RiskRadar;
